import type { ApiClient } from '@sentinel/api-client';
import { readExpiresAt, rotateAccessToken } from './access-token-refresh';

export interface AccessTokenSchedulerOptions {
  /** Refresh this many seconds before the access token expires. Default: 90. */
  leadSeconds?: number;
  /** Wait this long before retrying on transient failure. Default: 30_000ms. */
  retryDelayMs?: number;
}

export interface SchedulerHandle {
  /** Stop the scheduler; clears any pending timer and the visibility listener. */
  stop(): void;
  /** Force an immediate re-evaluation (e.g., after the SPA learns expires_at changed). */
  bump(): void;
}

const DEFAULT_LEAD_SECONDS = 90;
const DEFAULT_RETRY_DELAY_MS = 30_000;

export function startAccessTokenScheduler(
  client: ApiClient,
  options: AccessTokenSchedulerOptions = {},
): SchedulerHandle {
  const leadMs = (options.leadSeconds ?? DEFAULT_LEAD_SECONDS) * 1000;
  const retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  // In-memory cache of the last known expires_at. Acts as a fallback when
  // localStorage is unavailable (Safari Private mode, storage quota exceeded)
  // so tick() does not see null and immediately re-rotate on every call.
  let cachedExpiresAt: number | null = null;

  const armImmediate = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(tick, 0);
  };

  const tick = async () => {
    // TOCTOU guard: stop() may have been called between the timer scheduling
    // and the callback firing. Bail before any work.
    if (stopped) return;
    // Prefer localStorage (cross-tab source of truth); fall back to the
    // in-memory cache for tabs where localStorage writes are swallowed.
    const expiresAt = readExpiresAt() ?? cachedExpiresAt;
    const now = Date.now();
    const refreshAt = (expiresAt ?? 0) - leadMs;
    if (expiresAt === null || refreshAt <= now) {
      let result;
      try {
        result = await rotateAccessToken(client);
      } catch {
        // Network / runtime exceptions (TypeError: Failed to fetch, CORS
        // failure, etc.) propagate as rejections — treat them the same as
        // an explicit 'transient-failure' so the scheduler keeps trying.
        if (stopped) return;
        timer = setTimeout(tick, retryDelayMs);
        return;
      }
      // TOCTOU guard: stop() may have been called while the await was
      // suspended. Re-check before scheduling anything new.
      if (stopped) return;
      if (result.status === 'transient-failure') {
        timer = setTimeout(tick, retryDelayMs);
        return;
      }
      if (result.status === 'unauthorized') {
        // Existing sentinel:unauthenticated dispatcher handles the navigation.
        // Latch `stopped = true` so a racing visibilitychange between this
        // return and the React effect cleanup running cannot re-arm tick and
        // fire a second /auth/token against the (already-revoked) refresh
        // token family — backend would treat that as reuse and nuke harder.
        stopped = true;
        return;
      }
      if (result.status === 'ok') {
        // Cache in memory so subsequent tick() calls can use this value even
        // when localStorage writes are swallowed (Safari Private mode, quota).
        cachedExpiresAt = result.expiresAt;
        // Schedule directly from the freshly-returned expiresAt. Do NOT round-
        // trip through localStorage here: if writeExpiresAt was silently
        // swallowed (Safari Private mode, storage quota), readExpiresAt would
        // return null, armImmediate would re-rotate, and we'd spin a hot loop.
        // In-memory state is the source of truth for this tab on the success
        // path; localStorage is only the cross-tab coordination channel.
        const delay = Math.max(0, result.expiresAt - leadMs - Date.now());
        timer = setTimeout(tick, delay);
        return;
      }
      // 'lost-race' — holder wrote localStorage and fired the storage event;
      // re-read to pick up their freshly-published expires_at.
      armImmediate();
      return;
    }
    timer = setTimeout(tick, refreshAt - now);
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      armImmediate();
    }
  };

  const hasDocument = typeof document !== 'undefined';
  if (hasDocument) {
    document.addEventListener('visibilitychange', onVisible);
  }
  armImmediate();

  return {
    stop() {
      stopped = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (hasDocument) {
        document.removeEventListener('visibilitychange', onVisible);
      }
    },
    bump() {
      if (stopped) return;
      armImmediate();
    },
  };
}
